import { Text, Heading, Dialog } from '@soteria/design-system-ve';

import * as styles from './MoreInfoDialog.css';

interface ProviderApplicationsMoreInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProviderApplicationsMoreInfoDialog({ isOpen, onClose }: ProviderApplicationsMoreInfoDialogProps) {
  return (
    <Dialog cancelAction={{ label: 'Close' }} isOpen={isOpen} size="xxxl" title="About Provider Applications" onClose={onClose}>
      <div className={styles.dialogContent}>
        <Text>
          Provider Applications define which OAuth2 authentication providers and client applications can access your community's API endpoints. Each provider
          must be assigned to a workflow type.
        </Text>

        <div className={styles.section}>
          <Heading level="h3">Workflow Types:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>
                <strong>Classification</strong> — Providers assigned to this type can access the /classify endpoint
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>
                <strong>Moderation</strong> — Providers assigned to this type can access the /reports endpoint
              </Text>
            </li>
            <li className={styles.listItem}>
              <Text>If the same provider needs access to both endpoints, you must add it as two separate entries — one for each workflow type.</Text>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Provider URL Configuration:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>Enter the base URL of your OAuth2 authentication provider</Text>
            </li>
            <li className={styles.listItem}>
              <Text>The system will automatically discover OpenID Connect configuration</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Supports popular providers like Azure AD, Google, Auth0, and custom OAuth2 servers</Text>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Client Application IDs:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>List the client IDs of applications authorized to access APIs for the selected workflow type</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Each provider can have multiple authorized client applications</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Only pre-approved client IDs will be granted access to your endpoints</Text>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Security Benefits:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>Prevents unauthorized access to your APIs by enforcing per-workflow-type provider access</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Ensures only trusted applications can access your endpoints</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Maintains audit trails of API access and usage</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Supports role-based access control through OAuth2 scopes</Text>
            </li>
          </ul>
        </div>

        <Text>
          This configuration is essential for securing your workflows and ensuring only authorized applications can access your community's API capabilities.
        </Text>
      </div>
    </Dialog>
  );
}
